import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';

import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {getLoginLinksByIds} from 'src/openapi/admin/apis/LoginLinkControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import {userSearchResultResultSelector} from '../../../users-groups-search-result/slices/user-search-result.slice';

import {
    LOGIN_LINKS_DOWNLOAD_FILE_NAME,
    LOGIN_LINKS_DOWNLOAD_TASK_NAME,
    LOGIN_LINKS_FILE_HEADERS,
} from './constants';


import type {Error} from 'src/openapi/admin/models/Error';
import type {ErrorPayload} from 'src/typedef';

type Props = {
    userIds: number[],
    children: (handler: () => void) => React.ReactNode,
    getSortedEntityIds: () => Promise<{
        type: string,
        payload: {
            data: number[] | string[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const GenerateLoginLinksTool = ({userIds, children, getSortedEntityIds}: Props) => {
    const apiDispatch = useAppDispatch();
    const result = useSelector(userSearchResultResultSelector);

    const getData = useCallback(async (selectedIds: number[]) => {
        let ids: number[] = [];
        let fetchingFailed = false;

        if (result && result.totalCount < MAX_SELECTED_ENTITIES) {
            const sortedEntityIdsResponse = await getSortedEntityIds();
            if (sortedEntityIdsResponse.payload.success) {
                const sortedEntityIds = sortedEntityIdsResponse.payload.data as number[];
                ids = sortedEntityIds.filter(id => selectedIds.includes(id));
            } else {
                fetchingFailed = true;
            }
        } else {
            ids = selectedIds;
        }

        if (fetchingFailed) {
            return Promise.resolve({type: '', payload: {
                success: false,
                error: true,
                data: {message: 'Failed to fetch data for the report'},
            } as ErrorPayload});
        } else {
            return apiDispatch(getLoginLinksByIds({getLoginLinksByIdsRequest: {ids}}));
        }
    }, [getSortedEntityIds, result, apiDispatch]);

    return (
        <ChunkedDownloadTool
            ids={userIds}
            taskName={LOGIN_LINKS_DOWNLOAD_TASK_NAME}
            fileName={LOGIN_LINKS_DOWNLOAD_FILE_NAME}
            fileHeaders={LOGIN_LINKS_FILE_HEADERS}
            getData={getData}
            chunkSize={250}>
            {handler => children(handler)}
        </ChunkedDownloadTool>
    );
};
